import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Check } from "lucide-react";

/**
 * Generic multi-select with chips and search.
 * Props:
 *   options: [{ value, label, sub? }]
 *   value: string[]                 (selected values)
 *   onChange: (string[]) => void
 *   placeholder?: string
 *   testid?: string
 *   maxChipsBeforeCollapse?: number (default 2)
 */
export default function MultiSelect({
    options = [],
    value = [],
    onChange,
    placeholder = "Seleccionar…",
    testid,
    maxChipsBeforeCollapse = 2,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const selected = useMemo(() => {
        const set = new Set(value || []);
        return (options || []).filter((o) => set.has(o.value));
    }, [options, value]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return (options || []).filter((o) => {
            return (
                (o.label || "").toLowerCase().includes(q) ||
                (o.sub || "").toLowerCase().includes(q)
            );
        });
    }, [options, query]);

    const toggle = (val) => {
        const set = new Set(value || []);
        if (set.has(val)) set.delete(val);
        else set.add(val);
        onChange?.(Array.from(set));
    };
    const removeOne = (val) => {
        onChange?.((value || []).filter((v) => v !== val));
    };
    const clearAll = () => onChange?.([]);

    const visible = selected.slice(0, maxChipsBeforeCollapse);
    const overflow = selected.length - visible.length;

    return (
        <div ref={ref} className="multi-select" data-testid={testid} style={{ position: "relative" }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="input"
                style={{
                    width: "100%", minHeight: 40, padding: "6px 30px 6px 8px",
                    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
                    cursor: "pointer", textAlign: "left", position: "relative",
                }}
                data-testid={testid ? `${testid}-trigger` : undefined}
            >
                {selected.length === 0 && <span className="text-muted text-sm">{placeholder}</span>}
                {visible.map((s) => (
                    <span
                        key={s.value}
                        style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "var(--bg-2)", border: "1px solid var(--border-strong)",
                            borderRadius: 999, padding: "2px 6px 2px 8px", fontSize: 11.5,
                        }}
                    >
                        {s.label}
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); removeOne(s.value); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); removeOne(s.value); } }}
                            title="Quitar"
                            style={{ cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center" }}
                            data-testid={testid ? `${testid}-remove-${s.value}` : undefined}
                        >
                            <X size={11} />
                        </span>
                    </span>
                ))}
                {overflow > 0 && (
                    <span className="text-muted text-xs" style={{ background: "var(--bg-2)", borderRadius: 999, padding: "2px 8px" }}>
                        +{overflow}
                    </span>
                )}
                <ChevronDown size={14} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            </button>

            {open && (
                <div
                    style={{
                        position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 60,
                        background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)", padding: 6, maxHeight: 280, overflow: "hidden",
                        display: "flex", flexDirection: "column",
                    }}
                    data-testid={testid ? `${testid}-popup` : undefined}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px" }}>
                        <input
                            type="text"
                            className="input"
                            placeholder="Buscar…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                            autoFocus
                            data-testid={testid ? `${testid}-search` : undefined}
                        />
                        {selected.length > 0 && (
                            <button type="button" onClick={clearAll} className="btn btn-ghost btn-sm" style={{ padding: "2px 6px", fontSize: 11 }} data-testid={testid ? `${testid}-clear` : undefined}>
                                Limpiar
                            </button>
                        )}
                    </div>
                    <div style={{ overflowY: "auto", maxHeight: 220 }}>
                        {filtered.length === 0 && (
                            <div className="text-muted text-sm" style={{ padding: 10 }}>Sin coincidencias</div>
                        )}
                        {filtered.map((o) => {
                            const checked = (value || []).includes(o.value);
                            return (
                                <div
                                    key={o.value}
                                    onClick={() => toggle(o.value)}
                                    role="option"
                                    aria-selected={checked}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        padding: "6px 8px", cursor: "pointer", borderRadius: 4,
                                        background: checked ? "rgba(220,38,38,0.08)" : "transparent",
                                    }}
                                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                                >
                                    <span style={{ width: 14, height: 14, border: "1px solid var(--border-strong)", borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", background: checked ? "var(--accent)" : "transparent" }}>
                                        {checked && <Check size={10} color="#fff" />}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13 }}>{o.label}</div>
                                        {o.sub && <div className="text-muted mono text-xs">{o.sub}</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
